import React, { useCallback, useMemo } from 'react'
import { PaymentBotSummary } from '../../components/PaymentBotDashboard/components/PaymentBotSummary/PaymentBotSummary'
import { CarrierDetail } from '../../components/PaymentBotDashboard/components/CarrierDetail/CarrierDetail'
import { usePaymentBotCarrierSummaryQuery } from '../../api/payment-bot-dashboard-api-client'
import type {
  DashboardView,
  CarrierIdSelection,
} from '../../components/PaymentBotDashboard/shared/types'
import { ALL_CARRIERS } from '../../components/PaymentBotDashboard/shared/constants'

interface PaymentBotDashboardTabProps {
  month: number
  year: number
  /** Controlled view state — 'summary' or 'detail'. */
  view: DashboardView
  onViewChange: (view: DashboardView) => void
  /** Controlled carrier selection — ALL_CARRIERS string or a carrier id. */
  detailCarrierId: CarrierIdSelection
  onDetailCarrierIdChange: (id: CarrierIdSelection) => void
}
export default function PaymentBotDashboardTab({
  month,
  year,
  view,
  onViewChange,
  detailCarrierId,
  onDetailCarrierIdChange,
}: PaymentBotDashboardTabProps) {
  const { data: summaryData, isLoading: isCarrierListLoading } =
    usePaymentBotCarrierSummaryQuery({
      month,
      year,
    })

  const availableCarrierRows = useMemo(
    () =>
      summaryData?.carriers.map((c) => ({
        carrierId: c.carrierId,
        carrierName: c.carrierName,
      })) ?? [],
    [summaryData?.carriers]
  )

  const showCarrier = useCallback(
    (carrierId: number) => {
      onDetailCarrierIdChange(carrierId)
      onViewChange('detail')
    },
    [onDetailCarrierIdChange, onViewChange]
  )

  const showAllCarriers = useCallback(() => {
    onDetailCarrierIdChange(ALL_CARRIERS)
    onViewChange('detail')
  }, [onDetailCarrierIdChange, onViewChange])

  const backToSummary = useCallback(
    () => onViewChange('summary'),
    [onViewChange]
  )

  if (view === 'detail') {
    return (
      <CarrierDetail
        carrierId={detailCarrierId}
        month={month}
        year={year}
        availableCarrierRows={availableCarrierRows}
        isCarrierListLoading={isCarrierListLoading}
        onBack={backToSummary}
      />
    )
  }
  return (
    <PaymentBotSummary
      month={month}
      year={year}
      onSelectCarrier={showCarrier}
      onViewAllCarriers={showAllCarriers}
    />
  )
}
